import { useCallback, useEffect, useState } from "react";
import { ApiKeyService } from "@/services/api/ApiKeyService";
import { ApiKey, ApiKeyWithSecret } from "@/types/api-keys";

export function useApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchApiKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const keys = await ApiKeyService.listApiKeys();
      setApiKeys(keys);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load API keys");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const createKey = async (name: string): Promise<ApiKeyWithSecret> => {
    const newKey = await ApiKeyService.createApiKey(name);
    await fetchApiKeys();
    return newKey;
  };

  const revokeKey = async (keyId: string): Promise<void> => {
    await ApiKeyService.revokeApiKey(keyId);
    await fetchApiKeys();
  };

  const deleteKey = async (keyId: string): Promise<void> => {
    await ApiKeyService.deleteApiKey(keyId);
    await fetchApiKeys();
  };

  return {
    apiKeys,
    isLoading,
    error,
    createKey,
    revokeKey,
    deleteKey,
    refetch: fetchApiKeys,
  };
}
